import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { debounce } from 'lodash';
import { isAuthenticated, logout } from '../../utils/auth';
import Logo from '../../components/images/logo.png'

// NEW: Import convertToRaw and draftToHtml for rendering saved Draft.js content
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { BACKEND_URL } from '../../Constants';

function CourseView() {

    const navigate = useNavigate();

    const handleLogout = () => {
        logout(() => {
            navigate('/'); // Redirect to home after logout
        });
    };

    const { id: courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openModuleIds, setOpenModuleIds] = useState({});
    const [openPageIds, setOpenPageIds] = useState({});
    const [quizAttemptHistory, setQuizAttemptHistory] = useState({});
    const [quizState, setQuizState] = useState({ // Combine all quiz-related state, initialize with default values
        quizActive: false,
        quizQuestions: [],
        currentQuestionIndex: 0,
        score: 0,
        activeQuizItemId: null,
        studentAnswers: {},
        selectedAnswer: null,
    });
    const [viewingQuizHistory, setViewingQuizHistory] = useState(null);
    const studentId = localStorage.getItem('userId');
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [enrollmentStatus, setEnrollmentStatus] = useState(null);
    const [courseCompletedMessage, setCourseCompletedMessage] = useState(false);
    const [quizStateLoaded, setQuizStateLoaded] = useState(false);
    
    const userRole = localStorage.getItem('role');
    const shouldShowCourseContent = isEnrolled || userRole === 'admin';

    // Helper function to safely parse JSON
    const safeJsonParse = (str, fallback = {}) => {
        try {
            return JSON.parse(str);
        } catch (e) {
            console.error('Error parsing JSON:', e);
            return fallback;
        }
    };

    const fetchCourseDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${ BACKEND_URL }/api/courses/${courseId}`);
            setCourse(response.data);
        } catch (err) {
            console.error('Error fetching course details:', err);
            setError('Failed to load course details.');
        } finally {
            setLoading(false);
        }
    };

    const fetchEnrollmentStatus = async () => {
        if (studentId) {
            try {
                const response = await axios.get(`${ BACKEND_URL }/api/enrollments/status?courseId=${courseId}&studentId=${studentId}`);
                if (response.data.exists) {
                    setIsEnrolled(true);
                    setEnrollmentStatus(response.data.status);
                } else {
                    setIsEnrolled(false);
                    setEnrollmentStatus(null);
                }
            } catch (err) {
                console.error("Error fetching enrollment status", err);
                setError("Failed to fetch enrollment status.");
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    };

    const loadCourseProgress = async () => {
        try {
            const response = await axios.get(`${ BACKEND_URL }/api/courses/${courseId}/progress?studentId=${studentId}`);
            if (response.data) {
                setOpenPageIds(response.data.viewedPages || {});
                setQuizAttemptHistory(response.data.quizAttempts || {});
                setOpenModuleIds(response.data.openModuleIds || {});
            }
        } catch (error) {
            console.error('Error loading course progress:', error);
        }
    };

    const loadQuizState = () => {
        const quizStateData = localStorage.getItem(`quizState_${courseId}_${studentId}`);
        const parsedState = safeJsonParse(quizStateData, {  // Default Quiz State
            quizActive: false,
            quizQuestions: [],
            currentQuestionIndex: 0,
            score: 0,
            activeQuizItemId: null,
            studentAnswers: {},
            selectedAnswer: null,
        });
        setQuizState(parsedState);
        setQuizStateLoaded(true);
    };


    useEffect(() => {
        fetchCourseDetails();
        fetchEnrollmentStatus();
    }, [courseId, studentId]);

    useEffect(() => {
        if (isEnrolled) {
            loadCourseProgress();
            loadQuizState();
        }
    }, [isEnrolled, courseId, studentId]);

    useEffect(() => {
        if (quizStateLoaded) { // Only save if quiz state has been loaded
            localStorage.setItem(`quizState_${courseId}_${studentId}`, JSON.stringify(quizState));
        }
    }, [quizState, courseId, studentId, quizStateLoaded]);


    const handlePageView = async (pageId, isLastPage = false) => {
        setOpenPageIds(prev => ({ ...prev, [pageId]: true }));
    
        const newViewedPages = { ...openPageIds, [pageId]: true };
    
        saveCourseProgressDebounced({
            viewedPages: newViewedPages,
            quizAttempts: quizAttemptHistory,
            openModuleIds: openModuleIds
        });
    
        // If this is the final page in the course, mark as completed
        if (isLastPage) {
            try {
                const response = await axios.put(`${ BACKEND_URL }/api/enrollments/complete`, {
                    courseId,
                    studentId
                });
                console.log('Course marked as completed:', response.data);
                setCourseCompletedMessage(true); // Optional UI flag
            } catch (err) {
                console.error('Failed to mark course as complete:', err);
            }
        }
    };

    const handleQuizComplete = (quizItemId, finalScore) => {
        const now = new Date();
        const formattedDate = now.toLocaleDateString() + ' ' + now.toLocaleTimeString();

        const updatedQuizAttemptHistory = {
            ...quizAttemptHistory,
            [quizItemId]: [
                ...(quizAttemptHistory[quizItemId] || []),
                {
                    completed: true,
                    score: finalScore,
                    total: quizState.quizQuestions.length,
                    date: formattedDate,
                }
            ]
        };

        setQuizAttemptHistory(updatedQuizAttemptHistory);
        saveCourseProgressDebounced({
            viewedPages: openPageIds,
            quizAttempts: updatedQuizAttemptHistory,
            openModuleIds: openModuleIds
        });
        setQuizState({  // Reset to initial state
            quizActive: false,
            quizQuestions: [],
            currentQuestionIndex: 0,
            score: 0,
            activeQuizItemId: null,
            studentAnswers: {},
            selectedAnswer: null,
        });
        localStorage.removeItem(`quizState_${courseId}_${studentId}`);
    };

    const saveCourseProgress = useCallback(async (progressData) => {
        try {
            await axios.put(`${ BACKEND_URL }/api/courses/${courseId}/progress`, {
                studentId,
                ...progressData
            });
            console.log('Course progress saved');
        } catch (error) {
            console.error('Error saving course progress:', error);
        }
    }, [courseId, studentId]);

    const saveCourseProgressDebounced = useCallback(debounce(saveCourseProgress, 1000), [saveCourseProgress]);


    if (loading) {
        return <div>Loading course details...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!course) {
        return <div>Course not found.</div>;
    }

    const handleEnroll = async () => {
        try {
            const response = await axios.post(`${ BACKEND_URL }/api/enrollments`, {
                courseId: courseId,
                studentId: studentId,
            });
            if (response.data.success) {
                setIsEnrolled(true);
                setEnrollmentStatus('pending');
                alert('Enrollment request sent.  Awaiting approval.');
            } else {
                setError('Failed to enroll in the course.');
            }
        } catch (err) {
            console.error("Error during enrollment:", err);
            setError('Failed to enroll in the course.');
        }
    };

    const visibleModules = course.modules ? course.modules.filter(module => !module.deleted) : [];

    const handleModuleClick = (moduleId, moduleIndex) => {
        if (userRole !== 'admin') {
            const isOpen = isModuleOpen(moduleId);

            if (!isOpen && canOpenModule(moduleId, moduleIndex)) {
                const updatedOpenModuleIds = { ...openModuleIds, [moduleId]: true };
                setOpenModuleIds(updatedOpenModuleIds);
                saveCourseProgressDebounced({
                    viewedPages: openPageIds,
                    quizAttempts: quizAttemptHistory,
                    openModuleIds: updatedOpenModuleIds
                });
            }
        }
    };

    const togglePage = (moduleId, pageId) => {
        if (userRole !== 'admin') {
            setOpenPageIds(prevOpenPageIds => {
                const updatedOpenPageIds = {
                    ...prevOpenPageIds,
                    [moduleId]: {
                        ...(prevOpenPageIds[moduleId] || {}),
                        [pageId]: !(prevOpenPageIds[moduleId] && prevOpenPageIds[moduleId][pageId]), // Toggle
                    },
                };
                saveCourseProgressDebounced({  // Save the updated openPageIds
                    viewedPages: updatedOpenPageIds,
                    quizAttempts: quizAttemptHistory,
                    openModuleIds: openModuleIds,
                });
                return updatedOpenPageIds;
            });
        }
    };

    const isModuleOpen = (moduleId) => {
        if (userRole === 'admin') return true;
        return !!openModuleIds[moduleId];
    };

    const hasQuizAttemptsForItem = (itemId) => {
        return quizAttemptHistory.hasOwnProperty(itemId) && quizAttemptHistory[itemId].length > 0;
    };

    const getQuizAttemptsForItem = (itemId) => {
        return quizAttemptHistory[itemId] || [];
    };

    const getQuizQuestionsForItem = (itemId, moduleId) => {
        return course.modules.find(m => m._id === moduleId)?.items.find(i => i._id === itemId)?.quiz || [];
    };

    const canOpenModule = (moduleId, moduleIndex) => {
        if (moduleIndex === 0) return true;
        const previousModuleId = visibleModules[moduleIndex - 1]?._id;
        const previousModule = visibleModules[moduleIndex - 1];
        const previousModuleOpen = isModuleOpen(previousModuleId);
        const allPreviousPagesOpen = previousModule && previousModule.items
            ? previousModule.items
                .filter(item => item.type === 'page' && item.page && !item.page.deleted)
                .every(pageItem => openPageIds[previousModuleId]?.[pageItem.page._id])
            : true;
        const hasQuizInPreviousModule = previousModule?.items?.some(item => item.type === 'quiz');
        const previousQuizCompleted = !hasQuizInPreviousModule || previousModule?.items?.filter(item => item.type === 'quiz').every(quizItem => hasQuizAttemptsForItem(quizItem._id));

        return previousModuleOpen && allPreviousPagesOpen && previousQuizCompleted;
    };

    const canOpenPage = (moduleId, pageId, pageIndex) => {
        //const moduleIsOpen = isModuleOpen(moduleId);
        //console.log(`canOpenPage for module ${moduleId}, page ${pageId} (index ${pageIndex}):`);
        //console.log(`  isModuleOpen: ${moduleIsOpen}`);
      if (!isModuleOpen(moduleId)) return false;
      const pagesInModule = visibleModules.find(m => m._id === moduleId)?.items?.filter(item => item.type === 'page' && item.page && !item.page.deleted) || [];
      if (pageIndex === 0) return true;
      const previousPageId = pagesInModule[pageIndex - 1]?.page?._id;
      return openPageIds[moduleId]?.[previousPageId];
  };

    const startQuiz = (quiz, itemId) => {
        setQuizState({
            quizActive: true,
            quizQuestions: quiz,
            currentQuestionIndex: 0,
            score: 0,
            activeQuizItemId: itemId,
            studentAnswers: {},
            selectedAnswer: null,
        });
    };

    const handleRetakeQuiz = (itemId, moduleId) => {
        const quiz = getQuizQuestionsForItem(itemId, moduleId);
        startQuiz(quiz, itemId);
    };

    const handleViewQuiz = (itemId) => {
        setViewingQuizHistory(itemId);
    };

    const handleCloseViewQuiz = () => {
        setViewingQuizHistory(null);
    };

    const handleAnswerSelect = (answer) => {
        setQuizState(prev => ({ ...prev, selectedAnswer: answer, studentAnswers: { ...prev.studentAnswers, [prev.currentQuestionIndex]: answer } }));
    };

    const handleNextQuestion = async () => {
        if (!quizState.quizQuestions[quizState.currentQuestionIndex]) return;

        const currentQuestion = quizState.quizQuestions[quizState.currentQuestionIndex];
        const isCorrect = quizState.selectedAnswer === currentQuestion.correctAnswer;

        const newScore = isCorrect ? quizState.score + 1 : quizState.score;

        if (quizState.currentQuestionIndex < quizState.quizQuestions.length - 1) {
            setQuizState(prev => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1, score: newScore, selectedAnswer: null }));
        } else {
            let finalScore = newScore;
            setQuizState(prev => ({ ...prev, quizActive: false, score: finalScore }));


            alert(`Quiz Finished! Your score: ${finalScore} / ${quizState.quizQuestions.length}`);

            // ✅ Check if this quiz was the final item in the course
            const allItems = visibleModules.flatMap(module => module.items.filter(item => !item.deleted));
            const lastItem = allItems[allItems.length - 1];
            const isFinalQuiz = lastItem && lastItem._id === quizState.activeQuizItemId;

            if (isFinalQuiz) {
                try {
                    const response = await axios.put(`${ BACKEND_URL }/api/enrollments/complete`, {
                        courseId,
                        studentId
                    });
                    console.log('Course marked as completed (quiz)', response.data);
                    setCourseCompletedMessage(true);
                } catch (err) {
                    console.error('Failed to mark course as completed after final quiz:', err);
                }
            }

            if (quizState.activeQuizItemId) {
                const now = new Date();
                const formattedDate = now.toLocaleDateString() + ' ' + now.toLocaleTimeString();
                const updatedQuizAttemptHistory = {
                    ...quizAttemptHistory,
                    [quizState.activeQuizItemId]: [
                        ...(quizAttemptHistory[quizState.activeQuizItemId] || []),
                        {
                            score: finalScore,
                            total: quizState.quizQuestions.length,
                            date: formattedDate,
                            answers: quizState.quizQuestions.map((q, index) => ({
                                question: q.question,
                                studentAnswer: quizState.studentAnswers[index],
                                correctAnswer: q.correctAnswer,
                            })),
                        },
                    ],
                };
                setQuizAttemptHistory(updatedQuizAttemptHistory);
                saveCourseProgressDebounced({
                    viewedPages: openPageIds,
                    quizAttempts: updatedQuizAttemptHistory,
                    openModuleIds: openModuleIds
                });
                setQuizState({  // Reset Quiz State
                    quizActive: false,
                    quizQuestions: [],
                    currentQuestionIndex: 0,
                    score: 0,
                    activeQuizItemId: null,
                    studentAnswers: {},
                    selectedAnswer: null,
                });
                localStorage.removeItem(`quizState_${courseId}_${studentId}`);
            }
        }
    };

    

    

    // Render Quiz
    if (quizStateLoaded && quizState && quizState.quizActive && quizState.quizQuestions && quizState.quizQuestions.length > 0 && quizState.currentQuestionIndex < quizState.quizQuestions.length) {
        const currentQuestion = quizState.quizQuestions[quizState.currentQuestionIndex];
        return (
            <div className="quiz-container">
                <h3>Quiz</h3>
                <p>Question {quizState.currentQuestionIndex + 1} / {quizState.quizQuestions.length}</p>
                <p><strong>{currentQuestion.question}</strong></p>
                <ul>
                    {currentQuestion.options.map((option, index) => (
                        <li key={index} className={quizState.selectedAnswer === option ? 'selected' : ''}>
                            <label>
                                <input
                                    type="radio"
                                    name="answer"
                                    value={option}
                                    checked={quizState.selectedAnswer === option}
                                    onChange={() => handleAnswerSelect(option)}
                                />
                                {option}
                            </label>
                        </li>
                    ))}
                </ul>
                <button onClick={handleNextQuestion} disabled={quizState.selectedAnswer === null}>
                    {quizState.currentQuestionIndex === quizState.quizQuestions.length - 1 ? 'Finish Quiz' : 'Next Question'}
                </button>
            </div>
        );
    }

    if (viewingQuizHistory) {
        const quizItemId = viewingQuizHistory;
        const lastAttempt = getQuizAttemptsForItem(quizItemId)?.[getQuizAttemptsForItem(quizItemId).length - 1];
        const originalQuiz = getQuizQuestionsForItem(quizItemId, visibleModules.find(m => m.items.some(i => i._id === quizItemId))?._id);

        if (lastAttempt && originalQuiz) {
            return (
                <div className="view-quiz-history">
                    <h3>Quiz Review</h3>
                    {originalQuiz.map((question, index) => {
                        const isIncorrect = lastAttempt.answers[index]?.studentAnswer !== question.correctAnswer;
                        console.log(`Question ${index + 1}:`);
                        console.log(`  Your Answer: ${lastAttempt.answers[index]?.studentAnswer}`);
                        console.log(`  Correct Answer: ${question.correctAnswer}`);
                        console.log(`  Is Incorrect: ${isIncorrect}`);
                        return (
                            <div key={index} className={`quiz-question-review ${isIncorrect ? 'incorrect' : ''}`}>
                                <p><strong>{question.question}</strong></p>
                                <p>Your Answer: {lastAttempt.answers[index]?.studentAnswer || 'Not Answered'}</p>
                                {isIncorrect && (
                                    <p className="incorrect-label">Incorrect</p>
                                )}
                                <ul>
                                    {question.options.map((option, optionIndex) => (
                                        <li key={optionIndex}>{option}</li>
                                    ))}
                                </ul>
                                <hr />
                            </div>
                        );
                    })}
                    <button onClick={handleCloseViewQuiz} className="btn btn-secondary btn-sm">Close Review</button>
                </div>
            );
        } else {
            return <p>Error loading quiz review.</p>;
        }
    }

    // Helper function to render content that might be Draft.js JSON or plain HTML
    const renderContentAsHtml = (content) => {
        if (!content) return null;
        try {
            // Attempt to parse as Draft.js raw content JSON
            const rawContent = JSON.parse(content);
            // If successful, convert to HTML
            return <div dangerouslySetInnerHTML={{ __html: draftToHtml(rawContent) }} />;
        } catch (e) {
            // If parsing fails, assume it's plain HTML or text
            return <div dangerouslySetInnerHTML={{ __html: content }} />;
        }
    };

    const allPages = visibleModules
    .flatMap(module => module.items.filter(item => item.type === 'page' && item.page && !item.page.deleted));
    const lastPageId = allPages?.[allPages.length - 1]?.page?._id;


    return (
        <>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Navigation Bar */}
            <nav style={{ backgroundColor: '#f8f9fa', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                {/* Logo (replace with your actual logo) */}
                <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#007bff' }}>
                <Link to="/"><img src={Logo} alt="Learning Aids" height={ 35 } /></Link>
                </div>
                {/* Navigation Links */}
                <div className='header-link-block'>
                <div className='navlist'>
                    <ul>
                    <li><Link to="/courses">Courses</Link></li>
                    </ul>
                </div>
                {isAuthenticated() ? (
                    <>
                    <button
                        style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', textDecoration: 'none' }}
                        onClick={handleLogout}
                    >
                        Logout
                    </button>
                    <button
                        style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', textDecoration: 'none' }}
                        onClick={() => navigate('/dashboard')}
                    >
                        Dashboard
                    </button>
                    </>
                ) : (
                    <>
                    <Link to="/login" style={{ color: '#007bff', textDecoration: 'none', marginRight: '15px' }}>Login</Link>
                    <Link to="/register" style={{ color: '#007bff', textDecoration: 'none' }}>Register</Link>
                    </>
                )}
                </div>
            </nav>
        <div className="course-view-container">
            {shouldShowCourseContent && (enrollmentStatus === 'approved' || enrollmentStatus === 'completed' || userRole === 'admin') && (
                <>
                    {/* to be use for admin */ }
                </>
            )}
            {isEnrolled && (courseCompletedMessage || enrollmentStatus === 'completed') && (
                <div className="alert alert-success mt-4">
                    🎉 Congratulations! You’ve completed the course!
                </div>
            )}
            {/* Display messages based on enrollment status */}
            {enrollmentStatus === 'pending' && (
                <>
                <div className="alert alert-warning alert-dismissible">
                    <div className="alert-icon">
					    <i className="icon-exclamation"></i>
				    </div>
                    <div className="alert-message p-3">Your enrollment is pending approval. Please wait for an administrator to review your request.</div>
                </div>
                </>
            )}

            {enrollmentStatus === 'rejected' && (
                <div className="alert alert-danger p-3" role="alert">
                    <p>Your enrollment for this course has been rejected by an administrator.</p>
                </div>
            )}

            {enrollmentStatus === 'cancelled' && (
                <div className="alert alert-warning p-3" role="alert">
                    <p>Your enrollment for this course has been cancelled.</p>
                </div>
            )}
            {!isEnrolled && (
                <button onClick={handleEnroll} className="btn btn-primary">Enroll in Course</button>
            )}
            {shouldShowCourseContent && (enrollmentStatus === 'approved' || enrollmentStatus === 'completed' || userRole === 'admin') && (
                <div>
                    <h3>Modules:</h3>
                    {visibleModules.map((module, moduleIndex) => (
                        <div key={module._id} className="module-item accordion-module">
                            <div
                                className="module-header"
                                onClick={() => handleModuleClick(module._id, moduleIndex)}
                                style={{
                                    cursor: canOpenModule(module._id, moduleIndex) ? 'pointer' : 'not-allowed',
                                    padding: '10px',
                                    borderBottom: '1px solid #eee',
                                    opacity: canOpenModule(module._id, moduleIndex) ? 1 : 0.6,
                                }}
                            >
                                <h4>{module.title} {isModuleOpen(module._id) ? '▲' : '▼'}</h4>
                                {renderContentAsHtml(module.description)}
                                {!canOpenModule(module._id, moduleIndex) && (
                                    <small style={{ color: 'red' }}>
                                        {moduleIndex > 0 && !isModuleOpen(visibleModules[moduleIndex - 1]?._id)
                                            ? 'Open the previous module first.'
                                            : moduleIndex > 0 && visibleModules[moduleIndex - 1]?.items?.some(item => item.type === 'quiz') && !visibleModules[moduleIndex - 1]?.items?.filter(item => item.type === 'quiz').every(quizItem => hasQuizAttemptsForItem(quizItem._id))
                                                ? 'Complete all quizzes in the previous module first.'
                                                : 'Open all pages in the previous module first.'}
                                    </small>
                                )}
                            </div>
                            {isModuleOpen(module._id) && (
                                <div className="module-content" style={{ paddingLeft: '20px', marginTop: '10px' }}>
                                    {module.items
                                    ?.filter((item) => !item.deleted && item.type === 'presentation' && item.presentation)
                                    .map((presentationItem) => (
                                        <div key={presentationItem._id} className="item-in-module presentation-item" style={{ marginBottom: '20px' }}>
                                        <h5>{presentationItem.presentation.title}</h5>
                                        <div
                                            className="presentation-embed"
                                            dangerouslySetInnerHTML={{ __html: presentationItem.presentation.embedCode }}
                                            style={{ marginTop: '10px' }}
                                        />
                                        </div>
                                    ))}
                                    {module.items
                                        ?.filter((item) => item.type === 'page' && item.page && !item.page.deleted)
                                        .map((pageItem, pageIndex) => {
                                            const pageOpened = openPageIds[module._id]?.[pageItem.page._id];
                                            const isLastPage = pageItem.page._id === lastPageId;
                                    
                                            return (
                                                <div key={pageItem._id} className="item-in-module page-item accordion-page">
                                                    <div
                                                        className="page-header"
                                                        onClick={() => {
                                                            const canOpen = canOpenPage(module._id, pageItem.page._id, pageIndex);
                                                            const isOpen = openPageIds[module._id]?.[pageItem.page._id];
                                                        
                                                            if (!isOpen && canOpen) {
                                                                togglePage(module._id, pageItem.page._id, pageIndex);
                                                                handlePageView(pageItem.page._id, isLastPage);
                                                            }
                                                            // 🚫 Do nothing if already open — prevents closing
                                                        }}
                                                        style={{
                                                            cursor: canOpenPage(module._id, pageItem.page._id, pageIndex) ? 'pointer' : 'not-allowed',
                                                            padding: '8px',
                                                            borderBottom: '1px dashed #ccc',
                                                            opacity: canOpenPage(module._id, pageItem.page._id, pageIndex) ? 1 : 0.6,
                                                        }}
                                                    >
                                                        <h5>{pageItem.page.title} {pageOpened ? '▲' : '▼'}</h5>
                                                        {!canOpenPage(module._id, pageItem.page._id, pageIndex) && (
                                                            <small style={{ color: 'red' }}>Open previous pages in this module first.</small>
                                                        )}
                                                    </div>
                                                    {userRole === 'admin' ? (<>
                                                        <div className="page-content" style={{ paddingLeft: '15px', marginTop: '10px' }}>
                                                            {renderContentAsHtml(pageItem.page.content)}
                                                        </div>
                                                    </>) : (<>
                                                    {pageOpened && (
                                                        <div className="page-content" style={{ paddingLeft: '15px', marginTop: '10px' }}>
                                                            {renderContentAsHtml(pageItem.page.content)}
                                                        </div>
                                                    )}
                                                    </>) }
                                                </div>
                                            );
                                        })}
                                    {module.items
    ?.filter((item) => !item.deleted && item.type === 'quiz')
    .map((quizItem) => {
        const quiz = quizItem.quiz;
        const itemId = quizItem._id;
        return (
            <div key={itemId} className="item-in-module quiz-item">
                {quizItem?.quizTitle ? (
                    <h5>Quiz: {quizItem.quizTitle}</h5>
                ) : (
                    <h5>Quiz</h5>
                )}
                {userRole === 'admin' ? (
                    <div>
                        <h6>Quiz Questions:</h6>
                        <ul>
                        {quiz.map((question, index) => (
                            <li key={index}>
                                <p><strong>Question:</strong> {question.question}</p>
                                <ul>
                                    {question.options.map((option, optionIndex) => (
                                        <li key={optionIndex}>
                                            {option} {option === question.correctAnswer && <span style={{ color: 'green' }}>(Correct Answer)</span>}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                        </ul>
                    </div>
                ) : (
                    // Render the existing quiz logic for non-admins
                    hasQuizAttemptsForItem(itemId) ? (
                        <div>
                            {getQuizAttemptsForItem(itemId).map((attempt, index) => (
                                <p key={index}>
                                    Quiz Taken - Result: {attempt.score} / {attempt.total} Date: {attempt.date}
                                </p>
                            ))}
                            <Link
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleViewQuiz(itemId);
                                }}
                                className="btn btn-info btn-sm"
                            >
                                View Quiz
                            </Link>
                            
                            <Link
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleRetakeQuiz(itemId, module._id);
                                }}
                                className="btn btn-secondary btn-sm"
                                style={{ 'display': 'none' }}
                            >
                                Retake Quiz
                            </Link>
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                if (isModuleOpen(module._id) &&
                                    Object.keys(openPageIds[module._id] || {}).length >=
                                    (module.items?.filter(item => item.type === 'page' && item.page && !item.page.deleted).length || 0)) {
                                    startQuiz(quiz, itemId);
                                }
                            }}
                            className="btn btn-primary"
                            disabled={
                                !isModuleOpen(module._id) ||
                                Object.keys(openPageIds[module._id] || {}).length <
                                (module.items?.filter(item => item.type === 'page' && item.page && !item.page.deleted).length || 0)
                            }
                        >
                            Start Quiz: {quiz?.[0]?.question.substring(0, 50)}...
                        </button>
                    )
                )}
            </div>
        )
    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>

        </div>
    </>
    );
}

export default CourseView;
